import { CSSProperties, FC, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SiteFooter } from "../../components/footer/SiteFooter";
import { MarketingHeader } from "../marketing/MarketingHeader";
import "./AgentPage.scss";

const waveShape = (index: number, waveIndex: number) => 28 + ((index * 19 + waveIndex * 23) % 63);

const AudioBars: FC<{ wave?: string; index?: number; className?: string }> = ({ wave = "case", index = 0, className = "" }) => {
  const bars = useMemo(() => {
    const count = wave === "hero" ? 38 : 31;
    return Array.from({ length: count }, (_, i) => ({
      height: waveShape(i, index),
      delay: -(i % 9) * 0.14,
      speed: 0.95 + (i % 5) * 0.13,
    }));
  }, [index, wave]);

  return (
    <div className={`audio-bars ${className}`} data-wave={wave}>
      {bars.map((bar, i) => (
        <i
          key={i}
          style={{
            "--h": `${bar.height}%`,
            "--delay": `${bar.delay}s`,
            "--speed": `${bar.speed}s`,
          } as CSSProperties}
        />
      ))}
    </div>
  );
};

const WaveBackground = () => (
  <div className="wave-bg" aria-hidden="true">
    <svg viewBox="0 0 800 440" preserveAspectRatio="none">
      <path d="M-20 145 C120 20 230 320 390 170 S630 20 840 170" />
      <path d="M-20 175 C120 45 230 350 390 200 S630 50 840 200" />
      <path d="M-20 205 C120 70 230 380 390 230 S630 80 840 230" />
      <path d="M-20 235 C120 95 230 410 390 260 S630 110 840 260" />
      <path d="M-20 265 C120 120 230 440 390 290 S630 140 840 290" />
      <path d="M-20 295 C120 145 230 470 390 320 S630 170 840 320" />
      <path d="M-20 325 C120 170 230 500 390 350 S630 200 840 350" />
    </svg>
  </div>
);

const caseCards = [
  {
    title: "曦仔",
    image: "/img/lychee-agent/case-xizai.png",
    alt: "曦仔 · 橘猫毛绒玩偶穿西装",
    tags: ["企业 IP 定制", "普通话"],
    desc: "让企业吉祥物成为可对话的品牌角色，持续讲述品牌故事。",
  },
  {
    title: "粤悦",
    image: "/img/lychee-agent/case-yueyue.png",
    alt: "粤悦 · 岭南醒狮头套3D形象",
    tags: ["广东 IP", "粤语对话"],
    desc: "用地道粤语自然对话，生动讲述岭南醒狮文化。",
  },
  {
    title: "Baby精灵",
    image: "/img/lychee-agent/case-baby.png",
    alt: "Baby精灵 · 橙色毛绒小精灵",
    tags: ["原创 IP", "儿童陪伴"],
    desc: "陪孩子听睡前故事、探索好奇问题的成长伙伴。",
  },
];

export const AgentPage: FC = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Lychee Voice · Lychee Agent";
    const root = rootRef.current;
    if (!root) return;

    const navigation = root.querySelector<HTMLElement>(".nav");
    const updateNavigationStyle = () => navigation?.classList.toggle("is-scrolled", window.scrollY > 8);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add("is-visible");
      });
    }, { threshold: 0.14 });
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest<HTMLAnchorElement>("a[href]");
      const href = anchor?.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("tel:")) return;
      if (anchor?.target === "_blank" || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (href.startsWith("/")) {
        event.preventDefault();
        navigate(href);
      }
    };

    updateNavigationStyle();
    window.addEventListener("scroll", updateNavigationStyle, { passive: true });
    root.querySelectorAll(".reveal").forEach((node) => observer.observe(node));
    root.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("scroll", updateNavigationStyle);
      observer.disconnect();
      root.removeEventListener("click", handleClick);
    };
  }, [navigate]);

  return (
    <div ref={rootRef} className="agent-page marketing">
      <MarketingHeader />
      <main id="top">
        <section className="hero" id="experience">
          <WaveBackground />
          <div className="hero-experience wrap">
            <h1 className="experience-title">体验 Lychee Agent 实时对话</h1>
            <p className="experience-guide">唤醒玩偶，体验连续对话、随时打断和多轮记忆。</p>
            <div className="experience-window">
              <div className="console-tabs"><span>实时对话</span><span className="console-live">LIVE</span></div>
              <iframe
                className="experience-frame"
                src="https://voice-agent-test-4an.pages.dev/"
                title="Lychee Voice 实时语音 Agent 对话体验"
                loading="lazy"
                allow="microphone *; camera *; autoplay *; clipboard-read *"
                sandbox="allow-scripts allow-same-origin allow-modals allow-popups"
              />
            </div>
          </div>
        </section>

        <section className="section soft reveal" id="cases">
          <div className="wrap">
            <h2 className="section-title">AI 玩具场景案例</h2>
            <p className="subtle">将企业 IP、文化形象和原创角色，变成拥有独特声音、性格与对话能力的智能玩偶。</p>
            <div className="case-grid">
              {caseCards.map((item, index) => (
                <article className="cap-card case-card" key={item.title}>
                  <div className="case-media"><img src={item.image} alt={item.alt} width="800" height="600" /></div>
                  <div className="case-body">
                    <h3>{item.title}</h3>
                    <div className="case-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                    <p>{item.desc}</p>
                    <AudioBars className="case-wave" wave={`case${index + 1}`} index={index + 4} />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="conversion wrap reveal" id="start">
          <div className="conversion-inner">
            <div className="conversion-copy">
              <h2>想让你的玩偶也开口说话？</h2>
              <p>无论是企业 IP、文化形象还是原创角色，<br />Lychee Voice 都能为它装上会思考的声音。</p>
            </div>
            <div className="conversion-actions">
              <button className="btn black agent-contact-trigger" type="button" aria-label={"\u8054\u7cfb\u6211\u4eec"}>
                {"\u8054\u7cfb\u6211\u4eec"}
                <img className="agent-contact-qr" src="/img/lychee-agent/20260731-183125.jpg" alt="Contact QR code" />
              </button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};
